import type { PipelineTile } from "../api";

/**
 * Where a `g`/`pg` gate sits — immediately after the named step's
 * `stage_key`, never appended at the end of the lane (UX-mockup prototype
 * v2, frame 1: "2 Core needs › G0 › 3 Router", "7 PDF intake › G1 ›
 * 8 Takeaways" …). Backend `stage_order` does not interleave gates with
 * steps, so the frontend re-orders for display only — it never re-sorts
 * step tiles themselves, and never touches a tone/count/rank.
 */
const GATE_AFTER_STEP: Record<string, string> = {
  g0_core_needs_approval: "s02_core_needs",
  g1_pdf_handoff: "s07_pdf_intake",
  g2_promote: "s10_manual_review",
  pg2_promote: "p11_policy_decision",
  g3_voice_brand: "s14_manual_content_review",
  g4_distribution_signoff: "s17_distribution_review",
};

/** True for a gate marker (`step: null`, key starting `g` or `pg`) — never an output. */
export function isGateTile(tile: PipelineTile): boolean {
  return tile.step === null && /^(g|pg)\d/.test(tile.stage_key);
}

/**
 * Re-orders one lane's already `stage_order`-sorted tiles so every gate sits
 * right after the step it belongs after, per `GATE_AFTER_STEP`. A gate with
 * no mapping (shouldn't happen) stays wherever `stage_order` put it.
 */
export function orderLaneWithGates(tiles: PipelineTile[]): PipelineTile[] {
  const gates = tiles.filter(isGateTile);
  const rest = tiles.filter((t) => !isGateTile(t));
  const result = [...rest];
  for (const gate of gates) {
    const afterKey = GATE_AFTER_STEP[gate.stage_key];
    const idx = afterKey ? result.findIndex((t) => t.stage_key === afterKey) : -1;
    if (idx === -1) {
      result.push(gate);
    } else {
      result.splice(idx + 1, 0, gate);
    }
  }
  return result;
}
